import { TextField } from '@material-ui/core'
import { loadStripe } from '@stripe/stripe-js'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button, CreditCardIcon, SepaIcon } from 'components/facets'
import { ActionCard } from 'components/facets/ActionCard'
import { useUser } from 'components/providers/UserProvider'
import { createStripeSession, setupSepaMandate, setUserOutsetaAttributes } from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import qs from 'query-string'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditCardPresenter } from '../account/CreditCardPresenter'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK || '')

export interface OnboardingPaymentMethodProps {
  handleNext: () => void
}

export const PaymentMethod = ({ handleNext }: OnboardingPaymentMethodProps): JSX.Element => {
  const { t } = useTranslation()
  const { user, fetchUser } = useUser()
  const { handle } = useErrorHandler()
  const [referralCode, setReferralCode] = useState(
    user?.referralCode || localStorage.getItem('referralCode') || ''
  )
  const [creditCardDialogOpen, setCreditCardDialogOpen] = useState(false)
  const stripeSessionId = qs.parse(location.search)?.session_id as string

  useEffect(() => {
    const handleStripeCallback = async (): Promise<void> => {
      if (stripeSessionId) {
        await setupSepaMandate(stripeSessionId)
        fetchUser()
      }
    }
    handleStripeCallback()
  }, [stripeSessionId])

  const startGettingSepaMandate = async (): Promise<void> => {
    try {
      const { sessionId } = await createStripeSession('finish-signup')
      const stripe = await stripePromise
      const { error } = await stripe!.redirectToCheckout({
        sessionId,
      })
      if (error) throw error
    } catch (err) {
      handle(err)
    }
  }

  const saveReferralCode = async (): Promise<void> => {
    const code = referralCode.trim()
    if (!code) return
    try {
      await setUserOutsetaAttributes({ referralCode: code })
    } catch (err) {
      handle(err)
    }
  }

  const handleClickPaymentMethod = async (method: 'sepa' | 'creditCard'): Promise<void> => {
    await saveReferralCode()
    if (method === 'sepa') {
      startGettingSepaMandate()
    } else if (method === 'creditCard') {
      setCreditCardDialogOpen(true)
    }
  }

  return (
    <>
      <div className="d-flex justify-content-center w-100">
        <div className="wizard-content mt-lg-5 container">
          <div className="row justify-content-between">
            <div className="col-lg-6">
              <h3 className="fw-normal">{t('onboarding.trialTitle')}</h3>
              <h4 className="mb-3 mb-lg-5 fw-light">{t('onboarding.paymentDescription')}</h4>
              <div className="d-flex flex-row align-items-end mb-3">
                <h6 className="fw-light m-0 p-0 pb-1 pe-4">{t('signup.doYouHaveReferral')}</h6>
                <TextField
                  style={{ width: '150px' }}
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  fullWidth
                  label={t('signup.referral')}
                />
              </div>
            </div>
            <div className="col-lg-6">
              <div className="mb-4">
                <ActionCard
                  variant="withDescription"
                  title={t('account.addSepaMandate')}
                  description={t('onboarding.addSepaDescription')}
                  button={
                    <Button
                      variant="contained"
                      onClick={() => handleClickPaymentMethod('sepa')}
                      startIcon={<SepaIcon style={{ fontSize: '1.6rem' }} />}
                      style={{
                        width: '132px',
                      }}
                    >
                      Add
                    </Button>
                  }
                />
              </div>

              <div className="mb-4">
                <ActionCard
                  variant="withDescription"
                  title={t('account.creditCard')}
                  description={t('onboarding.addCredCardDescription')}
                  button={
                    <Button
                      variant="contained"
                      onClick={() => handleClickPaymentMethod('creditCard')}
                      startIcon={<CreditCardIcon style={{ fontSize: '1.6rem' }} />}
                      style={{
                        width: '132px',
                      }}
                    >
                      Add
                    </Button>
                  }
                />
              </div>

              <ActionCard
                variant="withDescription"
                title={t('onboarding.skipStep')}
                description={t('onboarding.skipBillingDescription')}
                button={
                  <Button
                    variant="outlined"
                    data-testid="skip"
                    onClick={async () => {
                      await saveReferralCode()
                      handleNext()
                    }}
                    style={{
                      width: '132px',
                    }}
                  >
                    Skip
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </div>
      <ClosableDialog
        open={creditCardDialogOpen}
        onClose={() => setCreditCardDialogOpen(false)}
        maxWidth="xl"
      >
        <div className="onboarding-credit-card">
          <CreditCardPresenter
            onFinish={() => {
              setCreditCardDialogOpen(false)
            }}
          />
        </div>
      </ClosableDialog>
    </>
  )
}
